'use client';

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  Sparkles,
  CalendarCheck,
  MessageCircle,
  Bot,
  ShoppingBag,
  Check,
  ImageIcon,
  Send,
  Package,
  Clock,
  Globe,
  UserCheck,
  Link2,
  CreditCard,
  Heart,
} from 'lucide-react';
import dynamicImport from 'next/dynamic';
import type { GalleryApi, GalleryItem } from './circular-gallery';
import { TypographyH2, TypographyLead } from '@/components/ui/typography';
import { Link } from '@/i18n/navigation';
import { localizedImageSrc } from '@/lib/localized-image';
import { SectionKicker } from '@/components/brand/section-kicker';
import { Reveal } from '@/components/motion/reveal';
import { cn } from '@/lib/utils';

/**
 * "Your whole stack, replaced" — five ornate arch gates, one per capability.
 *
 * Desktop (motion allowed): a curved gallery slider. Scroll slides the next gate to
 * the centre; once centred it expands and the whole screen fades into that gate's
 * own colour, revealing the chapter — one story in three beats (what goes in, what
 * Kasbly does, what comes out). Scroll on and it reverts to the slider.
 *
 * Reduced motion gets the calm static grid.
 */

const gold = (chunks: ReactNode) => <span className="text-brand-gold">{chunks}</span>;
const pad = (n: number) => String(n).padStart(2, '0');

// ogl (WebGL) only ships when the cinematic branch actually mounts — the
// static grid (SSR / reduced-motion) never pays for it.
const CircularGallery = dynamicImport(
  () => import('./circular-gallery').then((m) => m.CircularGallery),
  { ssr: false },
);

const DOOR_SRCS = [
  '/stack-door-1-v2.webp',
  '/stack-door-2-v2.webp',
  '/stack-door-3-v2.webp',
  '/stack-door-4-v2.webp',
  '/stack-door-5-v2.webp',
] as const;

/** Per-chapter background scene + strength (dedicated art runs full-strength). */
const CHAPTER_BG: { src: string; opacity: string; tint?: string }[] = [
  { src: '/stack-bg-frame-cream.webp', opacity: 'opacity-55', tint: '#4d5839' }, // sage — darker-sage silhouette
  { src: '/stack-sell-bg.webp', opacity: 'opacity-30' }, // peach — slight pink wash
  { src: '/stack-bg-gold-2.webp', opacity: 'opacity-55', tint: '#b97f2a' }, // gold — darker-gold silhouette
  { src: '/stack-sell-bg.webp', opacity: 'opacity-15' }, // dark green — website chat
  { src: '/stack-bg-frame-terra.webp', opacity: 'opacity-100' }, // terracotta — arch frame
];

/** Where each chapter's "Learn more" leads — the product-page deep dive. */
const CHAPTER_LINKS = [
  '/products/create#studio',
  '/products/create#publish',
  '/products/sell#inbox',
  '/products/sell#chat',
  '/products/sell#checkout',
] as const;

const THEME = [
  { bg: '#889070', fg: '#1c1c1c', accent: '#0f3d2e', title: '#3f6b4a' }, // sage — content
  { bg: '#f8b890', fg: '#1c1c1c', accent: '#0f3d2e', title: '#c2562f' }, // peach — scheduling
  { bg: '#f0b858', fg: '#1c1c1c', accent: '#0f3d2e', title: '#a9740c' }, // gold — inbox
  { bg: '#286050', fg: '#fbf7ee', accent: '#d4a017', title: '#1f4d3a' }, // dark green — website chat
  { bg: '#c45a3a', fg: '#fbf7ee', accent: '#f4d58a', title: '#a8462a' }, // terracotta — sell
] as const;

/** In → Kasbly → outcome, one icon per beat. */
const FLOW_ICONS = [
  [Package, Sparkles, ImageIcon],
  [CalendarCheck, Clock, Send],
  [MessageCircle, Bot, Check],
  [Globe, Bot, UserCheck],
  [ShoppingBag, Link2, CreditCard],
] as const;

type Beat = { title: string; sub: string };
type Story = { lead: string; flow?: Beat[]; steps?: string[] };
type Door = { title: string; desc: string; replaces: string; story: Story };

/** Localised micro-copy used INSIDE the illustrated scenes (i18n, never hardcoded). */
type SceneCopy = {
  days: string[];
  time: string;
  caption: string;
  tags: string[];
  question: string;
  reply: string;
  chatQuestion: string;
  chatReply: string;
  product: string;
  price: string;
  paid: string;
};

function DoorFace({
  src,
  door,
  replacesLabel,
}: {
  src: string;
  door: Door;
  replacesLabel: string;
}) {
  return (
    <>
      <Image
        src={src}
        alt=""
        fill
        sizes="(min-width: 1024px) 220px, 45vw"
        className="pointer-events-none select-none object-contain"
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center px-[12cqw] pt-[66cqw] pb-[26cqw] text-center">
        <span className="block font-display text-[9cqw] font-medium leading-[1.15] text-primary">
          {door.title}
        </span>
        <span className="mt-[3.5cqw] block text-[5.8cqw] leading-[1.4] text-muted-foreground">
          {door.desc}
        </span>
        <span className="mt-[8cqw] block text-[4.4cqw] font-semibold uppercase tracking-[0.1em] text-muted-foreground/70">
          {replacesLabel}
        </span>
        <span className="mt-[1.5cqw] block text-[5.4cqw] leading-[1.4] text-foreground/85">
          {door.replaces}
        </span>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------
 * Bespoke chapter scenes — hand-drawn from zero, one per capability.
 * Fully pictorial (skeleton bars stand in for copy) so they work in any
 * language; animated with the section's existing motion vocabulary.
 * ------------------------------------------------------------------ */

/** Direction-aware learn-more arrow (decorative). */
function ArrowRightIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden className="size-3.5 rtl:-scale-x-100">
      <path
        d="M3 8h10m0 0-4-4m4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Skeleton copy bar. */
function Bar({ w, className }: { w: string; className?: string }) {
  return <span className={cn('block h-2 rounded-full bg-muted', className)} style={{ width: w }} />;
}

/** Typing indicator — three breathing dots. */
function Dots({ className }: { className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1', className)} aria-hidden>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="size-1.5 rounded-full bg-current"
          style={{ animation: 'typing-dot 1.3s infinite', animationDelay: `${i * 0.16}s` }}
        />
      ))}
    </span>
  );
}

/** Gold four-point spark, twinkling. */
function Spark({ className, delay = 0 }: { className?: string; delay?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      className={cn('size-6 fill-current text-brand-gold', className)}
      style={{ animation: 'twinkle 2.6s ease-in-out infinite', animationDelay: `${delay}s` }}
    >
      <path d="M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z" />
    </svg>
  );
}

/** A stylised sand-and-gold bloom (the brand's saffron motif, hand-drawn). */
function Bloom({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" aria-hidden className={className}>
      {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
        <ellipse
          key={a}
          cx="32"
          cy="17"
          rx="6.5"
          ry="13"
          transform={`rotate(${a} 32 32)`}
          className="fill-secondary stroke-primary"
          strokeWidth="1.2"
        />
      ))}
      <circle cx="32" cy="32" r="6" className="fill-brand-gold" />
    </svg>
  );
}

/** 01 · Content creation — three drafts fanned: two behind, the hero on top. */
function SceneContent({ c }: { c: SceneCopy }) {
  return (
    <div className="relative mx-auto w-full max-w-[26rem]">
      {/* the drafts waiting behind, left and right */}
      <div
        aria-hidden
        className="absolute -start-14 top-10 w-3/5 -rotate-6 rounded-xl bg-card/85 p-4 shadow-card"
      >
        <div className="grid aspect-square place-items-center rounded-lg bg-accent">
          <Bloom className="size-16" />
        </div>
        <Bar w="70%" className="mt-3" />
        <Bar w="45%" className="mt-2" />
      </div>
      <div
        aria-hidden
        className="absolute -end-14 top-10 w-3/5 rotate-6 rounded-xl bg-card/85 p-4 shadow-card"
      >
        <div className="grid aspect-square place-items-center rounded-lg bg-accent">
          <Bloom className="size-16" />
        </div>
        <Bar w="60%" className="mt-3" />
        <Bar w="40%" className="mt-2" />
      </div>

      {/* the hero draft, front and centre */}
      <div className="relative mx-auto w-[72%] rounded-xl bg-card p-5 text-foreground shadow-hero">
        <div className="flex items-center gap-2.5">
          <span className="grid size-9 place-items-center rounded-full bg-accent">
            <Bloom className="size-6" />
          </span>
          <Bar w="5rem" />
          <span className="ms-auto flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1.5 text-primary">
            <Sparkles className="size-3.5" aria-hidden />
            <Dots />
          </span>
        </div>
        <div className="relative mt-3 grid aspect-[4/3] place-items-center overflow-hidden rounded-lg bg-accent">
          <Bloom className="size-24" />
          <Spark className="absolute bottom-2 start-2 size-5" />
        </div>
        <div className="mt-3 flex items-center gap-3 text-foreground/50">
          <Heart className="size-5" aria-hidden />
          <MessageCircle className="size-5" aria-hidden />
          <Send className="size-5" aria-hidden />
        </div>
        <span className="mt-2.5 block text-small font-medium">{c.caption}</span>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {c.tags.map((tag) => (
            <span key={tag} className="rounded-sm bg-accent px-2 py-0.5 text-caption text-primary">
              {tag}
            </span>
          ))}
        </div>
      </div>
      <Spark className="absolute -start-4 -top-4" delay={0.4} />
    </div>
  );
}

/** 02 · Scheduling — the content-calendar artwork, placed still. */
function SceneSchedule(_props: { c: SceneCopy }) {
  const locale = useLocale();
  return (
    <div className="relative mx-auto w-full lg:-ms-[14%] lg:w-[128%] lg:max-w-none">
      <Image
        src={localizedImageSrc('/stack-schedule.webp', locale)}
        alt=""
        aria-hidden
        width={1310}
        height={924}
        className="h-auto w-full select-none"
      />
    </div>
  );
}

/** 03 · Inbox & replies — the unified-inbox artwork, placed still. */
function SceneInbox(_props: { c: SceneCopy }) {
  const locale = useLocale();
  return (
    <div className="relative mx-auto w-full lg:-ms-[10%] lg:w-[122%] lg:max-w-none">
      <Image
        src={localizedImageSrc('/stack-inbox.webp', locale)}
        alt=""
        aria-hidden
        width={1308}
        height={888}
        className="h-auto w-full select-none"
      />
    </div>
  );
}

/** 04 · Website chat — a full storefront in a browser, the widget selling on its own. */
function SceneChat({ c }: { c: SceneCopy }) {
  return (
    <div className="relative mx-auto w-full max-w-[34rem]">
      <div className="relative overflow-hidden rounded-xl bg-card text-foreground shadow-hero">
        {/* browser chrome */}
        <div className="flex items-center gap-3 border-b border-border px-5 py-3">
          <span className="flex gap-1.5">
            {[0, 1, 2].map((i) => (
              <span key={i} className="size-2 rounded-full bg-muted" />
            ))}
          </span>
          <span className="mx-auto h-5 w-40 rounded-full bg-muted" />
          <Globe className="size-4 text-foreground/40" aria-hidden />
        </div>
        {/* storefront: nav, hero band, product shelf */}
        <div className="p-5 pe-56">
          <div className="flex items-center gap-3">
            <span className="grid size-7 place-items-center rounded-full bg-accent">
              <Bloom className="size-4" />
            </span>
            <Bar w="2.5rem" />
            <Bar w="2.5rem" />
            <ShoppingBag className="ms-auto size-4 text-foreground/40" aria-hidden />
          </div>
          <div className="mt-4 flex items-center gap-4 rounded-lg bg-accent px-5 py-4">
            <Bloom className="size-10 shrink-0" />
            <div className="flex-1 space-y-2">
              <Bar w="60%" className="bg-primary/20" />
              <Bar w="40%" />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            {[0, 1].map((i) => (
              <div key={i} className="rounded-lg border border-border p-3">
                <span className="grid h-14 place-items-center rounded-sm bg-muted">
                  <Bloom className="size-8" />
                </span>
                <span className="mt-2 block truncate text-caption font-medium">{c.product}</span>
                <span className="block text-caption font-semibold text-primary">{c.price}</span>
              </div>
            ))}
          </div>
        </div>
        {/* the chat widget, docked inside the page like the real thing */}
        <div className="absolute bottom-3 end-3 top-14 flex w-48 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-raised">
          <div className="flex items-center gap-2 bg-primary px-3 py-2 text-primary-foreground">
            <Bot className="size-3.5" aria-hidden />
            <span className="h-2 w-14 rounded-full bg-primary-foreground/45" />
            <span
              className="ms-auto size-2 rounded-full bg-brand-gold"
              style={{ animation: 'twinkle 1.8s ease-in-out infinite' }}
            />
          </div>
          <div className="flex-1 space-y-1.5 p-2.5">
            <div className="w-fit max-w-[90%] rounded-lg rounded-bl-sm bg-muted px-2.5 py-1.5 text-caption">
              {c.chatQuestion}
            </div>
            <div className="ms-auto w-fit max-w-[92%] rounded-lg rounded-br-sm bg-primary px-2.5 py-1.5 text-caption text-primary-foreground">
              {c.chatReply}
            </div>
            <div className="flex items-center gap-2 ps-1 pt-0.5 text-primary">
              <Dots />
            </div>
          </div>
          <div className="m-2 flex items-center gap-2 rounded-full bg-muted px-3 py-1.5">
            <span className="h-2 flex-1 rounded-full bg-foreground/15" />
            <Send className="size-3.5 text-primary" aria-hidden />
          </div>
        </div>
      </div>
      <Spark className="absolute -start-3 -top-3" delay={0.6} />
    </div>
  );
}

/** 05 · Sell & checkout — the checkout-card artwork (order chip baked in). */
function SceneSell(_props: { c: SceneCopy }) {
  const locale = useLocale();
  return (
    <div className="relative mx-auto w-full max-w-[27rem]">
      <Image
        src={localizedImageSrc('/stack-sell-card.webp', locale)}
        alt=""
        aria-hidden
        width={875}
        height={918}
        className="h-auto w-full select-none"
      />
    </div>
  );
}

const SCENES = [SceneContent, SceneSchedule, SceneInbox, SceneChat, SceneSell] as const;

/** Full-screen chapter — the real product illustration demonstrates the story;
 *  beside it, the title, one plain sentence and a three-beat checklist. The
 *  floating outcome card echoes the home hero's notification language. */
function Chapter({
  door,
  storyKicker,
  index,
  theme,
  copy,
  formats,
  learnMore,
}: {
  door: Door;
  storyKicker: string;
  index: number;
  theme: { bg: string; fg: string; accent: string };
  copy: SceneCopy;
  formats?: string[];
  learnMore: string;
}) {
  const icons = FLOW_ICONS[index] ?? FLOW_ICONS[0];
  const flow = door.story.flow ?? [];
  const outcome = flow[flow.length - 1];
  const Scene = SCENES[index] ?? SCENES[0];
  return (
    <div
      data-overlay
      style={{ backgroundColor: theme.bg, color: theme.fg }}
      className="pointer-events-none absolute inset-0 z-20 flex items-center opacity-0"
    >
      {/* Chapter background art — tinted chapters render the artwork as a
          silhouette coloured a step darker than the chapter bg (tone-on-tone,
          like the comps); the rest keep a light wash of the cream scene. */}
      {CHAPTER_BG[index]?.tint ? (
        <span
          aria-hidden
          className={`pointer-events-none absolute inset-0 ${CHAPTER_BG[index]!.opacity}`}
          style={{
            backgroundColor: CHAPTER_BG[index]!.tint,
            maskImage: `url(${CHAPTER_BG[index]!.src})`,
            maskSize: 'contain',
            maskPosition: 'bottom center',
            maskRepeat: 'no-repeat',
            WebkitMaskImage: `url(${CHAPTER_BG[index]!.src})`,
            WebkitMaskSize: 'contain',
            WebkitMaskPosition: 'bottom center',
            WebkitMaskRepeat: 'no-repeat',
          }}
        />
      ) : (
        <Image
          src={CHAPTER_BG[index]?.src ?? '/stack-sell-bg.webp'}
          alt=""
          aria-hidden
          fill
          sizes="100vw"
          className={`pointer-events-none select-none object-contain object-bottom ${CHAPTER_BG[index]?.opacity ?? 'opacity-15'}`}
        />
      )}
      <div className="container-brand relative grid items-center gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16">
        <div className="text-center lg:text-start">
          <span
            data-reveal
            className="block text-overline"
            style={{ opacity: 0.7, color: 'inherit' }}
          >
            {pad(index + 1)} · {storyKicker}
          </span>
          <span
            data-reveal
            className="mt-3 block font-display text-[clamp(2.1rem,4.6vw,4rem)] font-medium leading-[1.02] text-current"
          >
            {door.title}
          </span>
          <span
            data-reveal
            className="mx-auto mt-4 block max-w-md text-body-lg lg:mx-0"
            style={{ opacity: 0.9 }}
          >
            {door.story.lead}
          </span>
          <ul className="mx-auto mt-8 flex w-fit flex-col gap-3 text-start lg:mx-0">
            {flow.map((beat, k) => {
              const Icon = icons[k] ?? Check;
              return (
                <li
                  key={beat.title}
                  data-reveal
                  className="flex items-center gap-3 rounded-lg px-4 py-3"
                  style={{ backgroundColor: `${theme.fg}0f` }}
                >
                  <span
                    className="grid size-8 shrink-0 place-items-center rounded-full"
                    style={
                      index === 4
                        ? { backgroundColor: theme.fg, color: theme.bg }
                        : { backgroundColor: `${theme.fg}14` }
                    }
                  >
                    <Icon className="size-4" aria-hidden />
                  </span>
                  <span className="text-body font-medium">{beat.title}</span>
                </li>
              );
            })}
          </ul>
          {index === 0 && formats?.length ? (
            <div
              data-reveal
              className="mx-auto mt-5 flex max-w-md flex-wrap justify-center gap-2 lg:mx-0 lg:justify-start"
            >
              {formats.map((f) => (
                <span
                  key={f}
                  className="rounded-full px-3 py-1.5 text-caption font-medium"
                  style={{ backgroundColor: `${theme.fg}14` }}
                >
                  {f}
                </span>
              ))}
            </div>
          ) : null}
          <div data-reveal className="mx-auto mt-6 w-fit lg:mx-0">
            <Link
              href={CHAPTER_LINKS[index] ?? '/products/create'}
              className="pointer-events-auto inline-flex items-center gap-1.5 text-small font-semibold underline-offset-4 transition-brand hover:underline"
              style={{ color: theme.fg }}
            >
              {learnMore}
              <ArrowRightIcon />
            </Link>
          </div>
          {index === 4 ? (
            <div
              data-reveal
              className="mx-auto mt-8 flex w-fit items-center gap-2.5 rounded-lg px-4 py-3 text-small lg:mx-0"
              style={{ backgroundColor: `${theme.fg}14` }}
            >
              <Spark className="relative" delay={0.4} />
              {door.desc}
            </div>
          ) : null}
        </div>

        <div data-reveal className="relative mx-auto hidden w-full max-w-[34rem] sm:block">
          <Scene c={copy} />
          {outcome && index !== 4 ? (
            <div
              className={
                index === 0
                  ? 'absolute -bottom-9 start-1/2 flex -translate-x-1/2 items-center gap-2.5 rounded-lg bg-card px-4 py-3 text-foreground shadow-raised rtl:translate-x-1/2'
                  : 'absolute -bottom-10 start-2 flex items-center gap-2.5 rounded-lg bg-card px-4 py-3 text-foreground shadow-raised'
              }
            >
              <span className="grid size-7 place-items-center rounded-full bg-primary text-primary-foreground">
                <Check className="size-4" aria-hidden />
              </span>
              <span className="text-small font-semibold">{outcome.title}</span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function Stack() {
  const t = useTranslations('stack');
  const locale = useLocale();
  const doors = t.raw('doors') as Door[];
  const storyKicker = t('storyKicker');
  const replacesLabel = t('replacesLabel');
  const sceneCopy = t.raw('scenes') as SceneCopy;
  const formats = t.raw('formats') as string[];

  // Gate content is stable within a locale — memoise so the WebGL context and its
  // baked textures are not rebuilt on a re-render.
  const items = useMemo<GalleryItem[]>(
    () =>
      (t.raw('doors') as Door[]).map((d, i) => ({
        image: DOOR_SRCS[i]!,
        title: d.title,
        desc: d.desc,
        replaces: d.replaces,
        color: THEME[i]!.bg,
        titleColor: THEME[i]!.title,
      })),
    [locale],
  );

  const [cinematic, setCinematic] = useState(false);
  const pinRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<GalleryApi | null>(null);

  useEffect(() => {
    // The WebGL pinned slider runs on every screen (mobile included) — the old
    // phone jank came from the 3 MB PNG door textures + uncapped DPR, both fixed
    // at the texture/renderer level. Reduced-motion still gets the static grid.
    const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setCinematic(!mqReduce.matches);
    sync();
    mqReduce.addEventListener('change', sync);
    return () => mqReduce.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (!cinematic) return;
    const pin = pinRef.current;
    if (!pin) return;

    gsap.registerPlugin(ScrollTrigger);
    const lenis = (
      window as unknown as {
        lenis?: {
          on: (e: string, cb: () => void) => void;
          off: (e: string, cb: () => void) => void;
          scrollTo: (
            y: number,
            o?: { duration?: number; easing?: (t: number) => number; immediate?: boolean },
          ) => void;
        };
      }
    ).lenis;
    if (lenis) lenis.on('scroll', ScrollTrigger.update);
    const html = document.documentElement;
    let settleCleanup: (() => void) | undefined;

    const ctx = gsap.context(() => {
      const overlays = Array.from(pin.querySelectorAll<HTMLElement>('[data-overlay]'));
      const head = pin.querySelector<HTMLElement>('[data-head]');
      const N = overlays.length;
      // Phones scroll by momentum flicks that cover thousands of pixels — give
      // the timeline more scroll length and much longer browse pauses so a
      // flick out of the previous section lands on the carousel, not mid-zoom.
      const mobile = window.matchMedia('(max-width: 1023.98px)').matches;
      const nav = { pos: 0 };
      const z = { val: 0 };
      const cf = { val: 0 };
      const pushGallery = () => {
        const api = apiRef.current;
        if (api) {
          api.setScroll(nav.pos * api.itemWidth());
          api.setZoom(z.val);
          api.setColorFade(cf.val);
        }
      };

      const tl = gsap.timeline({ defaults: { ease: 'power2.inOut' }, onUpdate: pushGallery });

      for (let i = 0; i < N; i++) {
        const overlay = overlays[i]!;
        const reveals = Array.from(overlay.querySelectorAll('[data-reveal]'));
        tl.addLabel(`g${i}`);
        // 1. slide the carousel so gate i is centred (flat during the slide)
        if (i === 0) tl.set(nav, { pos: 0 }, `g${i}`);
        else {
          tl.set(z, { val: 0 }, `g${i}`);
          tl.set(cf, { val: 0 }, `g${i}`);
          tl.to(nav, { pos: i, duration: 0.7, ease: 'power3.inOut' }, `g${i}`);
        }
        // 2. browse rest — a real pause on the centred gate AND a snap point, so a
        //    half-started zoom settles back here instead of auto-completing forward.
        //    Phones get longer pauses (the scroll gate below catches flick entries
        //    at the start; the pauses keep in-section flicks from skipping beats).
        tl.addLabel(`b${i}`);
        tl.to({}, { duration: mobile ? (i === 0 ? 1.6 : 0.9) : 0.25 });
        // 3. the gate tints to its own colour in place, then the zoom carries it
        //    across the whole screen — one continuous cinematic move
        tl.addLabel(`zoom${i}`);
        if (head) tl.to(head, { opacity: 0, duration: 0.3 }, `zoom${i}`);
        tl.to(cf, { val: 1, duration: 0.55, ease: 'power2.inOut' }, `zoom${i}`);
        tl.to(z, { val: 1, duration: 0.85, ease: 'power3.inOut' }, `zoom${i}+=0.2`);
        // 4. the chapter settles in on that colour (soft scale + fade, staggered copy)
        tl.fromTo(
          overlay,
          { opacity: 0, scale: 1.04 },
          { opacity: 1, scale: 1, duration: 0.55, ease: 'power3.out' },
          `zoom${i}+=0.72`,
        );
        if (reveals.length)
          tl.from(
            reveals,
            { opacity: 0, y: 36, stagger: 0.1, duration: 0.6, ease: 'power3.out' },
            '>-0.25',
          );
        tl.addLabel(`h${i}`);
        // 5. hold to read
        tl.to({}, { duration: 1 });
        // 6. revert — content out, gate shrinks back, then un-tints into the carousel
        if (reveals.length)
          tl.to(reveals, { opacity: 0, y: -18, duration: 0.25, ease: 'power2.in' });
        tl.to(overlay, { opacity: 0, scale: 1.02, duration: 0.4 }, '>-0.12');
        tl.to(z, { val: 0, duration: 0.65, ease: 'power3.inOut' }, '<');
        tl.to(cf, { val: 0, duration: 0.45 }, '>-0.32');
        if (head) tl.to(head, { opacity: 1, duration: 0.35 }, '<0.1');
      }

      const dur = tl.totalDuration();
      // rest points: every browsed gate AND every chapter — snap glides to the
      // NEAREST one, so stopping mid-transition never lingers awkwardly
      const snap = doors
        .flatMap((_, i) => [tl.labels[`b${i}`] ?? 0, tl.labels[`h${i}`] ?? 0])
        .map((time) => time / dur);

      const st = ScrollTrigger.create({
        animation: tl,
        trigger: pin,
        start: 'top top',
        // Desktop was 1.15 (≈5.75 screens of scroll to clear 5 gates) — too long;
        // 0.85 makes each gate turn over in far less scroll so it feels snappy,
        // closer to how the phone's scroll-gated version reads. Phones keep their
        // longer track (the scroll gate + browse pauses depend on it).
        end: () => '+=' + window.innerHeight * N * (mobile ? 1.4 : 0.85),
        pin: true,
        // low scrub so the animation tracks the scroll (no lingering auto-complete),
        // with just enough smoothing to feel buttery
        scrub: 0.35,
        anticipatePin: 1,
        // Scroll gate (phones): one smoothed flick out of the previous section
        // travels thousands of pixels and would land mid-zoom. FAST entries
        // (velocity-checked, so gentle scrolling is never touched) get caught
        // just inside the section start — held for a beat to swallow the
        // flick's residual events — so the chapter always opens on the
        // carousel. Resting a hair INSIDE the pin means the next scroll does
        // not re-cross the trigger, so the user is never gated twice, and
        // scrolling back up is never blocked.
        onEnter: mobile
          ? (self) => {
              if (Math.abs(self.getVelocity()) < 1800) return;
              const target = self.start + 80;
              const clamp = () => {
                if (window.scrollY <= target + 2) return;
                if (lenis) lenis.scrollTo(target, { immediate: true });
                else window.scrollTo(0, target);
              };
              clamp();
              const t0 = performance.now();
              const hold = setInterval(() => {
                if (performance.now() - t0 > 450) return clearInterval(hold);
                clamp();
              }, 30);
            }
          : undefined,
        onToggle: (self) => {
          if (self.isActive) html.setAttribute('data-stack-pinned', '');
          else html.removeAttribute('data-stack-pinned');
        },
      });

      // Settle-assist THROUGH Lenis (never GSAP snap — two scroll authorities
      // fight and cause the delayed "auto-complete" jank): when scrolling goes
      // quiet mid-transition, glide to the nearest rest (browsed gate or chapter).
      // Wheel-tuned — on touch it fights native momentum and reads as the page
      // jumping on its own, so phones skip it.
      if (lenis && !mobile) {
        let settleTimer: ReturnType<typeof setTimeout> | undefined;
        const settle = () => {
          clearTimeout(settleTimer);
          if (!st.isActive) return;
          settleTimer = setTimeout(() => {
            const p = st.progress;
            let nearest = snap[0] ?? 0;
            for (const r of snap) if (Math.abs(r - p) < Math.abs(nearest - p)) nearest = r;
            if (Math.abs(nearest - p) > 0.004) {
              lenis.scrollTo(st.start + (st.end - st.start) * nearest, {
                duration: 0.8,
                easing: (t: number) => 1 - Math.pow(1 - t, 4),
              });
            }
          }, 140);
        };
        lenis.on('scroll', settle);
        settleCleanup = () => {
          clearTimeout(settleTimer);
          lenis.off('scroll', settle);
        };
      }
      pushGallery();
    }, pinRef);

    return () => {
      settleCleanup?.();
      if (lenis) lenis.off('scroll', ScrollTrigger.update);
      html.removeAttribute('data-stack-pinned');
      ctx.revert();
    };
  }, [cinematic, doors.length]);

  // Calm static grid — SSR default + reduced motion.
  if (!cinematic) {
    return (
      <section className="section bg-background">
        <div className="container-brand">
          <Reveal className="mx-auto max-w-2xl text-center">
            <SectionKicker>{t('kicker')}</SectionKicker>
            <TypographyH2 className="mx-auto mt-4 max-w-2xl border-b-0 pb-0 font-display text-foreground">
              {t.rich('heading', { hl: gold })}
            </TypographyH2>
            <TypographyLead className="mx-auto mt-3 max-w-xl">{t('lede')}</TypographyLead>
          </Reveal>
          <div className="mx-auto mt-12 grid max-w-[1120px] grid-cols-2 items-start gap-5 sm:grid-cols-3 lg:mt-14 lg:grid-cols-5 lg:gap-3">
            {doors.map((door, i) => (
              <Reveal
                key={door.title}
                index={i}
                className="mx-auto w-full max-w-[260px] lg:max-w-none"
              >
                <div className="@container relative aspect-[622/1284] w-full">
                  <DoorFace src={DOOR_SRCS[i]!} door={door} replacesLabel={replacesLabel} />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Cinematic pinned experience — curved slider → zoom into coloured detail.
  return (
    <section className="bg-background">
      <div ref={pinRef} className="relative h-screen overflow-hidden bg-background">
        {/* browse: heading + curved gallery slider */}
        <div data-head className="absolute inset-x-0 top-0 z-10 pt-14 text-center">
          <div className="container-brand">
            <SectionKicker>{t('kicker')}</SectionKicker>
            <TypographyH2 className="mx-auto mt-3 max-w-2xl border-b-0 pb-0 font-display text-foreground">
              {t.rich('heading', { hl: gold })}
            </TypographyH2>
          </div>
        </div>
        {/* Hanging vines — top corners, behind the gates. The art mirror follows
            the PHYSICAL side (left vine always mirrored, right always upright):
            logical start/end swap sides in RTL, so without the rtl:/ltr: split
            Arabic showed each corner's artwork inverted. */}
        <Image
          src="/fg-vine.webp"
          alt=""
          aria-hidden
          width={514}
          height={1351}
          className="pointer-events-none absolute end-0 top-0 z-[5] hidden w-44 select-none rtl:-scale-x-100 lg:block xl:w-56"
        />
        <Image
          src="/fg-vine.webp"
          alt=""
          aria-hidden
          width={514}
          height={1351}
          className="pointer-events-none absolute start-0 top-0 z-[5] hidden w-44 select-none ltr:-scale-x-100 lg:block xl:w-56"
        />
        <div className="absolute inset-0">
          <CircularGallery
            items={items}
            replacesLabel={replacesLabel}
            rtl={locale === 'ar'}
            onReady={(api) => (apiRef.current = api)}
          />
        </div>

        {/* chapters the slider fades into */}
        {doors.map((door, i) => (
          <Chapter
            key={door.title}
            door={door}
            storyKicker={storyKicker}
            index={i}
            theme={THEME[i]!}
            copy={sceneCopy}
            formats={formats}
            learnMore={t('learnMore')}
          />
        ))}
      </div>
    </section>
  );
}
